import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Edit, Save, Loader2, Trash2, Wand2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Judge-side feedback panel: lists dancers, filter by role/search, write/save feedback per round.
 *
 * Props:
 * - api: API base URL
 * - judge: { judge_id, competition_id, name, judge_role }
 * - competition: { competition_id, rounds }
 */
export default function JudgeFeedbackPanel({ api, judge, competition }) {
  const [dancers, setDancers] = useState([]);
  const [feedbackMap, setFeedbackMap] = useState({}); // dancer_id -> { round_number -> {feedback_id, text} }
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [round, setRound] = useState(1);
  const [editing, setEditing] = useState(null); // dancer_id currently being edited
  const [editText, setEditText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestingFor, setSuggestingFor] = useState(null); // dancer_id being suggested for
  const [suggestions, setSuggestions] = useState([]); // string[]
  const [language, setLanguage] = useState("en"); // "en" | "el"

  const rounds = competition?.rounds || [];

  const refresh = useCallback(async () => {
    if (!judge?.competition_id) return;
    setLoading(true);
    try {
      const [dancersRes, fbRes] = await Promise.all([
        axios.get(`${api}/dancers?competition_id=${judge.competition_id}`),
        axios.get(`${api}/feedback?judge_id=${judge.judge_id}`),
      ]);
      setDancers(dancersRes.data || []);
      const map = {};
      (fbRes.data || []).forEach((fb) => {
        map[fb.dancer_id] = map[fb.dancer_id] || {};
        map[fb.dancer_id][fb.round_number] = { feedback_id: fb.feedback_id, text: fb.text };
      });
      setFeedbackMap(map);
    } catch (e) {
      toast.error("Failed to load feedback data");
    } finally {
      setLoading(false);
    }
  }, [api, judge?.judge_id, judge?.competition_id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredDancers = useMemo(() => {
    const s = search.trim().toLowerCase();
    return dancers
      .filter((d) => roleFilter === "all" || d.role === roleFilter)
      .filter((d) =>
        !s ||
        (d.name || "").toLowerCase().includes(s) ||
        String(d.bib_number || "").includes(s) ||
        (d.country || "").toLowerCase().includes(s)
      )
      .sort((a, b) =>
        a.role === b.role
          ? (a.bib_number || 0) - (b.bib_number || 0)
          : a.role.localeCompare(b.role)
      );
  }, [dancers, roleFilter, search]);

  const startEdit = (dancer) => {
    setEditing(dancer.dancer_id);
    const existing = feedbackMap[dancer.dancer_id]?.[round]?.text || "";
    setEditText(existing);
    setSuggestions([]);
  };

  const save = async (dancer) => {
    if (!editText.trim()) {
      toast.error("Feedback cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${api}/feedback?judge_id=${judge.judge_id}`,
        {
          competition_id: judge.competition_id,
          dancer_id: dancer.dancer_id,
          round_number: round,
          text: editText.trim(),
        }
      );
      setFeedbackMap((prev) => ({
        ...prev,
        [dancer.dancer_id]: {
          ...(prev[dancer.dancer_id] || {}),
          [round]: { feedback_id: res.data.feedback_id, text: res.data.text },
        },
      }));
      setEditing(null);
      setEditText("");
      toast.success("Feedback saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (dancer) => {
    const fb = feedbackMap[dancer.dancer_id]?.[round];
    if (!fb) return;
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await axios.delete(`${api}/feedback/${fb.feedback_id}?judge_id=${judge.judge_id}`);
      setFeedbackMap((prev) => {
        const next = { ...prev };
        if (next[dancer.dancer_id]) {
          const inner = { ...next[dancer.dancer_id] };
          delete inner[round];
          next[dancer.dancer_id] = inner;
        }
        return next;
      });
      toast.success("Feedback deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const requestSuggestions = async (dancer) => {
    setSuggestingFor(dancer.dancer_id);
    setSuggestions([]);
    try {
      const r = await axios.post(
        `${api}/feedback/suggest?judge_id=${judge.judge_id}`,
        { dancer_id: dancer.dancer_id, language }
      );
      const sug = r.data?.suggestions || [];
      if (!sug.length) {
        toast.error("AI returned no suggestions");
      } else {
        setSuggestions(sug);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestion failed");
    } finally {
      setSuggestingFor(null);
    }
  };

  const applySuggestion = (text) => {
    setEditText((prev) => (prev ? prev + "\n\n" + text : text));
    setSuggestions([]);
  };

  return (
    <div className="space-y-4" data-testid="judge-feedback-panel">
      <div className="glass p-4 rounded-xl space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
            <Input
              placeholder="Search name / bib / country"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-black/30 border-white/10 text-white"
              data-testid="feedback-search"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="bg-black/30 border-white/10 text-white" data-testid="feedback-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="Leader">Leaders</SelectItem>
              <SelectItem value="Follower">Followers</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(round)} onValueChange={(v) => setRound(parseInt(v, 10))}>
            <SelectTrigger className="bg-black/30 border-white/10 text-white" data-testid="feedback-round-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {rounds.map((rname, idx) => (
                <SelectItem key={rname} value={String(idx + 1)}>
                  {rname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[#A1A1AA] text-xs">
          Writing feedback for <b className="text-white">{rounds[round - 1] || `Round ${round}`}</b>.
          One feedback per dancer per round (auto-replaces).
        </p>
      </div>

      {loading ? (
        <div className="text-center text-[#A1A1AA] py-8">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : filteredDancers.length === 0 ? (
        <p className="text-center text-[#52525B] py-8">No dancers match.</p>
      ) : (
        <div className="space-y-2" data-testid="feedback-dancer-list">
          {filteredDancers.map((d) => {
            const fb = feedbackMap[d.dancer_id]?.[round];
            const isEditing = editing === d.dancer_id;
            return (
              <div
                key={d.dancer_id}
                className="glass rounded-lg p-3"
                data-testid={`feedback-dancer-${d.dancer_id}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-md flex items-center justify-center font-bold text-sm"
                      style={{
                        background: d.role === "Leader" ? "#FFD70022" : "#FF007F22",
                        color: d.role === "Leader" ? "#FFD700" : "#FF007F",
                      }}
                    >
                      #{d.bib_number || "?"}
                    </div>
                    <div>
                      <p className="text-white font-semibold leading-tight">{d.name}</p>
                      <p className="text-[#52525B] text-xs">
                        {d.role}
                        {d.country ? ` · ${d.country}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {fb && !isEditing && (
                      <span className="text-emerald-400 text-xs uppercase tracking-wider font-bold">
                        ✓ Saved
                      </span>
                    )}
                    {!isEditing ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-outline"
                        onClick={() => startEdit(d)}
                        data-testid={`feedback-edit-${d.dancer_id}`}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        {fb ? "Edit" : "Add"}
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          className="btn-gold"
                          onClick={() => save(d)}
                          disabled={saving}
                          data-testid={`feedback-save-${d.dancer_id}`}
                        >
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setEditing(null); setEditText(""); }}
                          className="text-[#A1A1AA]"
                          data-testid={`feedback-cancel-${d.dancer_id}`}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    {fb && !isEditing && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(d)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`feedback-delete-${d.dancer_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="btn-outline"
                        onClick={() => requestSuggestions(d)}
                        disabled={suggestingFor === d.dancer_id}
                        data-testid={`feedback-suggest-${d.dancer_id}`}
                      >
                        {suggestingFor === d.dancer_id ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Wand2 className="w-3 h-3 mr-1 text-[#FFD700]" />
                        )}
                        Suggest with AI
                      </Button>
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger className="h-8 w-24 bg-black/30 border-white/10 text-white text-xs" data-testid={`feedback-language-${d.dancer_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="el">Ελληνικά</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {suggestions.length > 0 && (
                      <div className="space-y-1.5" data-testid={`feedback-suggestions-${d.dancer_id}`}>
                        {suggestions.map((s, i) => (
                          <button
                            key={`${d.dancer_id}-sug-${i}-${s.slice(0, 20)}`}
                            type="button"
                            onClick={() => applySuggestion(s)}
                            className="block w-full text-left text-xs p-2 rounded bg-[#FFD700]/5 border border-[#FFD700]/20 hover:bg-[#FFD700]/10 text-white"
                            data-testid={`feedback-suggestion-${d.dancer_id}-${i}`}
                          >
                            <span className="text-[#FFD700] font-bold mr-1">{i + 1}.</span>
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      placeholder="Write your honest feedback for this dancer…"
                      rows={3}
                      maxLength={2000}
                      className="bg-black/40 border-white/10 text-white"
                      data-testid={`feedback-textarea-${d.dancer_id}`}
                    />
                  </div>
                ) : fb ? (
                  <p className="mt-2 text-[#D4D4D8] text-sm whitespace-pre-wrap pl-12">
                    {fb.text}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
